/** What you steer the replay with: which index, which session, which
 * minute.
 *
 * ONE ROW, on purpose. Every pixel spent here is taken from the chain and
 * the basket below it, and this is chrome — you set it and then look
 * somewhere else. Labels are `aria-label` rather than visible text: the
 * three controls are a dropdown of index names, a dropdown of dates and a
 * clock, none of which is ambiguous on sight, and the visible captions
 * cost a whole extra row of height to say what the values already say.
 *
 * The date list comes from the archive rather than a calendar. Indices
 * joined the capture at different times, so offering a free date picker
 * would let you ask for a NIFTY day that exists and a FINNIFTY day that
 * does not, and the only feedback would be an unexplained empty chain.
 *
 * Expiry lives with the CHAIN, not here — it selects which contracts the
 * table shows, so it belongs above the table it changes.
 */

import type { ReactElement } from "react";
import { TimePicker } from "./TimePicker";
import { cn } from "@/lib/cn";
import type { SimSessions } from "@/lib/simulatorApi";
import { SessionPicker } from "./SessionPicker";
import {
  BACK_STEPS,
  FORWARD_STEPS,
  atLimit,
  atSessionLimit,
  type ClockStep,
} from "@/lib/simulatorClock";

const BTN = "btn btn-secondary px-2 py-1 text-[11px]";

interface StepButtonsProps {
  steps: readonly ClockStep[];
  at: string;
  /** Earliest minute the clock may reach — the first entry in the basket,
   * or the session open when there is none. */
  floor: string;
  onStep: (minutes: number) => void;
}

function StepButtons({ steps, at, floor, onStep }: Readonly<StepButtonsProps>): ReactElement {
  return (
    <>
      {steps.map((s) => (
        <button
          key={s.label}
          type="button"
          className={BTN}
          title={
            atLimit(at, s.minutes, floor) && s.minutes < 0 && floor !== "09:15"
              ? `The basket was opened at ${floor} — the clock cannot go behind it`
              : undefined
          }
          disabled={atLimit(at, s.minutes, floor)}
          onClick={() => {
            onStep(s.minutes);
          }}
        >
          {s.label}
        </button>
      ))}
    </>
  );
}

interface DayButtonProps {
  label: string;
  days: number;
  sessions: string[];
  tradeDate: string;
  onStepDay: (days: number) => void;
}

/** A day step walks the archive's session list, so it is dead on the first
 * and last captured session rather than on a calendar bound. */
function DayButton({
  label,
  days,
  sessions,
  tradeDate,
  onStepDay,
}: Readonly<DayButtonProps>): ReactElement {
  return (
    <button
      type="button"
      className={BTN}
      disabled={atSessionLimit(sessions, tradeDate, days)}
      onClick={() => {
        onStepDay(days);
      }}
    >
      {label}
    </button>
  );
}

export interface ControlsProps {
  indices: SimSessions[];
  underlying: string;
  tradeDate: string;
  sessions: string[];
  at: string;
  floor: string;
  onUnderlying: (v: string) => void;
  onTradeDate: (v: string) => void;
  onAt: (v: string) => void;
  onStep: (minutes: number) => void;
  onStepDay: (days: number) => void;
  autoRunning: boolean;
  onAutoRun: () => void;
}

function IndexPicker(props: Readonly<ControlsProps>): ReactElement {
  return (
    <select
      aria-label="Index"
      className="input h-7 w-32 py-0 text-xs"
      value={props.underlying}
      onChange={(e) => {
        props.onUnderlying(e.target.value);
      }}
    >
      {props.indices.map((i) => (
        <option key={i.underlying} value={i.underlying}>
          {i.underlying} ({i.sessions.length})
        </option>
      ))}
    </select>
  );
}

function SessionField(props: Readonly<ControlsProps>): ReactElement {
  const dates = props.indices.find((i) => i.underlying === props.underlying)?.sessions ?? [];
  return <SessionPicker value={props.tradeDate} sessions={dates} onChange={props.onTradeDate} />;
}

export function SimulatorControls(props: Readonly<ControlsProps>): ReactElement {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <IndexPicker {...props} />

      <DayButton
        label="-1d"
        days={-1}
        sessions={props.sessions}
        tradeDate={props.tradeDate}
        onStepDay={props.onStepDay}
      />
      <StepButtons steps={BACK_STEPS} at={props.at} floor={props.floor} onStep={props.onStep} />

      <SessionField {...props} />
      <TimePicker at={props.at} floor={props.floor} onAt={props.onAt} />

      <StepButtons steps={FORWARD_STEPS} at={props.at} floor={props.floor} onStep={props.onStep} />
      <DayButton
        label="+1d"
        days={1}
        sessions={props.sessions}
        tradeDate={props.tradeDate}
        onStepDay={props.onStepDay}
      />

      <button
        type="button"
        className={cn(
          "btn ml-1 px-2.5 py-1 text-[11px]",
          props.autoRunning ? "btn-danger" : "btn-primary",
        )}
        onClick={props.onAutoRun}
      >
        {props.autoRunning ? "Stop" : "Auto run"}
      </button>
    </div>
  );
}

/** Expiry tabs, shown above the chain they select. */
export function ExpiryTabs({
  expiry,
  expiries,
  onExpiry,
}: Readonly<{
  expiry: string | null;
  expiries: string[];
  onExpiry: (v: string) => void;
}>): ReactElement | null {
  if (expiries.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {expiries.map((e) => (
        <button
          key={e}
          type="button"
          className={cn(
            "rounded-ctl px-2.5 py-1 text-[11px] font-medium transition-colors",
            e === expiry
              ? "bg-accent-bg text-accent"
              : "text-ink-secondary hover:bg-surface-2 hover:text-ink",
          )}
          onClick={() => {
            onExpiry(e);
          }}
        >
          {e}
        </button>
      ))}
    </div>
  );
}
